/**
 * What the progress screen shows while the interpretive report is generated. The backend reports
 * fine-grained steps; the reader sees five phases that say what is happening in plain words.
 */

export type GenerationPhase = 'readingAnswers' | 'calculating' | 'analyzing' | 'writing' | 'assembling';

export const GENERATION_PHASES: readonly GenerationPhase[] = [
  'readingAnswers',
  'calculating',
  'analyzing',
  'writing',
  'assembling',
];

const PHASE_OF_STEP: Record<string, GenerationPhase> = {
  GATHERING_DATA: 'readingAnswers',
  BUILDING_CONTEXT: 'readingAnswers',
  PROFILING_DIMENSIONS: 'calculating',
  ANALYZING_ARCHETYPE: 'analyzing',
  DEFINING_TRAJECTORY: 'analyzing',
  CREATING_QUESTIONS: 'writing',
  ANALYZING_VARIANCE: 'writing',
  INTERPRETING_DIMENSIONS: 'writing',
  PERSISTING: 'assembling',
};

export type PhaseState = 'done' | 'active' | 'todo';

export type PhaseView = { phase: GenerationPhase; state: PhaseState };

/** The phase in progress; a step the screen does not know counts as the first phase */
export const currentPhaseIndex = (step: string | null): number =>
  GENERATION_PHASES.indexOf((step && PHASE_OF_STEP[step]) || 'readingAnswers');

const stateOf = (index: number, current: number): PhaseState => {
  if (index < current) return 'done';
  return index === current ? 'active' : 'todo';
};

/** Every phase with whether it is done, in progress or still to come. When ready, all are done. */
export const phasesView = (step: string | null, ready: boolean): PhaseView[] => {
  const current = ready ? GENERATION_PHASES.length : currentPhaseIndex(step);
  return GENERATION_PHASES.map((phase, index) => ({ phase, state: stateOf(index, current) }));
};

/** Which of the three screens to show: generating, ready to open, or failed */
export type GenerationScreen = 'working' | 'ready' | 'failed';

export const generationScreenOf = ({ failed, ready }: { failed: boolean; ready: boolean }): GenerationScreen => {
  if (failed) return 'failed';
  return ready ? 'ready' : 'working';
};

export const clampPercent = (progress: number) => Math.round(Math.min(Math.max(progress, 0), 100));

/** Below this, too little of the work is done to say how long the rest takes */
const MIN_PROGRESS_FOR_ESTIMATE = 10;

/**
 * Whole minutes left, from how long the work so far has taken. Null when there is too little to
 * go on, so the screen leaves the estimate out rather than guess.
 */
export const minutesLeft = (progress: number, startedAt: string | null, now: Date): number | null => {
  if (!startedAt || progress < MIN_PROGRESS_FOR_ESTIMATE || progress >= 100) return null;
  const elapsedMs = now.getTime() - new Date(startedAt).getTime();
  if (!(elapsedMs > 0)) return null;
  const remainingMs = (elapsedMs * (100 - progress)) / progress;
  return Math.max(1, Math.ceil(remainingMs / 60_000));
};
